import { Box, Text, useMediaQuery, theme } from '@everlywell/ui-kit';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import React from 'react';

export type CarouselItem = {
  id: number;
  text: string;
  iconName: IconKey;
  iconColor: string;
};

export type ReassuranceCarouselProps = {
  carouselItems: CarouselItem[];
  isAnimated: boolean;
};

/**
 * ReassuranceCarousel component is used to display reassurance items in a carousel
 * @param props ReassuranceCarouselProps object containing carouselItems array
 * @returns JSX.Element containing reassurance items
 */
function ReassuranceCarousel(props: ReassuranceCarouselProps): JSX.Element {
  const { carouselItems, isAnimated } = props;
  const [isSmallerThanDesktop] = useMediaQuery(
    `(max-width: ${theme.breakpoints.desktop})`,
  );
  const carouselIndices = isAnimated || isSmallerThanDesktop ? [0, 1] : [0];

  return (
    <SectionContainer
      size={['fluid', 'fluid', 'fluid', 'fluid', 'ultra']}
      alignSelf="center"
      marginX={[null, null, 'auto']}
      centerContent
    >
      <Box
        whiteSpace="nowrap"
        transform="translate3d(0, 0, 0)"
        display="flex"
        width={['100%', null, isAnimated ? '100%' : 'auto']}
        overflow="hidden"
        // Pauses the animation on hover (desktop only)
        sx={{
          '&:hover': {
            '.animated-container': {
              'animation-play-state': 'paused',
            },
          },
        }}
      >
        {carouselIndices.map((outerIndex) => (
          <Box
            height="100%"
            transform="translate3d(0, 0, 0)"
            display="flex"
            animation={
              isAnimated || isSmallerThanDesktop
                ? 'animateContainer 30s linear infinite'
                : 'none'
            }
            className="animated-container"
            sx={{
              '@keyframes animateContainer': {
                '0%': {
                  transform: 'translateX(0%)',
                },
                '100%': {
                  transform: 'translateX(-100%)',
                },
              },
            }}
            key={`outer-box-${outerIndex}`}
          >
            {carouselItems.map(({ id, text, iconName, iconColor }, index) => {
              const isLastItem = index === carouselItems.length - 1;
              const isFirstItem = index === 0;
              return (
                <Box
                  key={id}
                  display="flex"
                  marginLeft={isFirstItem ? [8, 16] : 0}
                  marginRight={isLastItem ? 0 : [8, 16]}
                  width="auto"
                >
                  <DynamicIcon
                    icon={iconName}
                    color={iconColor}
                    marginRight={2}
                  />
                  <Text as="span">{text}</Text>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </SectionContainer>
  );
}

export default ReassuranceCarousel;
